/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import BoardIcon from '@atlaskit/icon/core/board';
import IconTile from '@atlaskit/icon/icon-tile';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	JIRA_PLANS_ADD_PROGRAM_BOARD_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { JiraPlansCreateProgramBoardPayload } from '@atlassian/conversation-assistant-message-actions-api/jira';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { EntityCard } from '../../common/ui/entity-card';
import { EntityTitle } from '../../common/ui/entity-title';
import { EntityValueGrid, EntityValueGridRow } from '../../common/ui/entity-value-grid';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';
import { jiraPlansActionData } from '../../common/utils/plan-action-data';

import messages from './messages';

const INTERACTION_SOURCE = 'jiraPlansAddProgramBoardRenderer';

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
	},
});

export const filterAddProgramBoardActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansCreateProgramBoard]>[] => {
	const filteredActions = actions.filter((action) => assertIsAddProgramBoardAction(action));
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansCreateProgramBoard]
	>[];
};

const assertIsAddProgramBoardAction = (action: MessageAction): MessageAction => {
	if (
		!jiraPlansActionData.isPlanActionKeyForRenderer(action.key, JIRA_PLANS_ADD_PROGRAM_BOARD_KEY)
	) {
		throw new Error('Invalid action key for AddProgramBoardAction');
	}
	return action;
};

export const AddProgramBoardAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansCreateProgramBoard>,
): JSX.Element | null => {
	const { actions } = props;

	const firstAction = useMemo(() => filterAddProgramBoardActions(actions)[0], [actions]);
	if (!firstAction) {
		return null;
	}

	return <AddProgramBoardActionContent {...props} firstAction={firstAction} />;
};

const AddProgramBoardActionContent = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansCreateProgramBoard> & {
		firstAction: ActionWith<
			ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansCreateProgramBoard]
		>;
	},
) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage, formatDate } = useIntl();

	const {
		getStatus,
		getReadonly,
		firstAction,
		onConfirm,
		onCancel,
		actions,
		sendMessageConfirmedTools,
		recipientAgentId,
		recipientAgentNamedId,
		conversationId,
		messageId,
	} = props;

	const isCustomAgent = !isDefaultRovoChat({
		agentId: recipientAgentId,
		agentNamedId: recipientAgentNamedId,
	});

	const { data } = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const invocationStatus = String(status ?? '').toLowerCase();
	const actionStatus = String(firstAction.status ?? '').toLowerCase();

	const isActionLoading = invocationStatus !== 'idle';
	const isSubmitting = invocationStatus === 'started';
	const isActionFinished = invocationStatus === 'finished' || actionStatus === 'finished';
	const buttonsDisabled = isActionLoading || readonly;

	const actionData =
		jiraPlansActionData.getPlanActionData<JiraPlansCreateProgramBoardPayload>(data);
	const handleConfirm = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-add-program-board-confirm',
				planId: actionData.planId,
				planName: actionData.planName,
				programBoardName: actionData.name,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed')
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onConfirm(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onConfirm,
		isCustomAgent,
	]);

	const handleCancel = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-add-program-board-cancel',
				planId: actionData.planId,
				planName: actionData.planName,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) => buildToolFromAction(action, 'cancelled')),
				},
			});
			return;
		}

		onCancel(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onCancel,
		isCustomAgent,
	]);

	const formatStartDate = useCallback(
		(start: number) =>
			formatDate(start, {
				month: 'numeric',
				day: 'numeric',
				year: '2-digit',
				hour: 'numeric',
				minute: '2-digit',
				hour12: true,
			})
				.replace('AM', 'am')
				.replace('PM', 'pm'),
		[formatDate],
	);

	if (
		!actionData.name ||
		actionData.start === undefined ||
		actionData.iterationLength === undefined ||
		actionData.iterationsCount === undefined
	) {
		return null;
	}

	return (
		<ActionCardWrapper isExpandable title={formatMessage(messages.cardTitle)} product="plan">
			<EntityCard>
				<EntityTitle
					icon={<IconTile label="" appearance="gray" size="small" icon={BoardIcon} />}
					title={actionData.name}
				/>
				<EntityValueGrid>
					<EntityValueGridRow
						label={`${formatMessage(messages.startDateLabel)}:`}
						value={formatStartDate(actionData.start)}
					/>
					<EntityValueGridRow
						label={`${formatMessage(messages.columnDurationLabel)}:`}
						value={formatMessage(messages.columnDurationValue, {
							count: actionData.iterationLength,
						})}
					/>
					<EntityValueGridRow
						label={`${formatMessage(messages.numberOfColumnsLabel)}:`}
						value={String(actionData.iterationsCount)}
					/>
				</EntityValueGrid>
			</EntityCard>

			{!isActionFinished && (
				<ActionCardFooter>
					<Box xcss={styles.footer}>
						<Button appearance="default" onClick={handleCancel} isDisabled={buttonsDisabled}>
							{formatMessage(messages.cancelButton)}
						</Button>
						<Button
							appearance="primary"
							onClick={handleConfirm}
							isDisabled={buttonsDisabled}
							isLoading={isSubmitting}
						>
							{formatMessage(messages.confirmButton)}
						</Button>
					</Box>
				</ActionCardFooter>
			)}
		</ActionCardWrapper>
	);
};
